import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { AlertTriangle, FileSignature, Loader2, LogOut, Ban, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContractRequired = ({ children }) => {
  const { user, token, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);

  useEffect(() => {
    checkAccessStatus();
  }, [user, token]);

  const checkAccessStatus = async () => {
    // Only check for team members (exclude affiliates who don't need contracts)
    if (!user || user.role !== 'team_member' || user.team_member_type === 'affiliate') {
      setChecking(false);
      setAccessStatus({ has_valid_contract: true, is_suspended: false });
      return;
    }

    try {
      const response = await axios.get(`${API}/member-contracts/check-access/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccessStatus(response.data);
    } catch (error) {
      console.error('Error checking access status:', error);
      // On error, allow access (fail-open for better UX)
      setAccessStatus({ has_valid_contract: true, is_suspended: false });
    } finally {
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500">Vérification de votre accès...</p>
        </div>
      </div>
    );
  }

  // If user is suspended, show suspension screen
  if (accessStatus && accessStatus.is_suspended) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-slate-100 p-4">
        <Card className="max-w-md w-full shadow-xl border-red-200">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Ban className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-xl text-red-800">Compte suspendu</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600">
              Votre compte est momentanément suspendu. Veuillez prendre contact avec l'agence pour plus d'informations.
            </p>
            
            {accessStatus.suspension_reason && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-700">
                  <span className="font-medium">Motif :</span> {accessStatus.suspension_reason}
                </p>
              </div>
            )}
            
            <div className="space-y-2 pt-4">
              <p className="text-sm text-slate-500 font-medium">Contactez l'agence :</p>
              <div className="flex flex-col gap-2">
                <a 
                  href="tel:+33000000000" 
                  className="flex items-center justify-center gap-2 p-3 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  <Phone className="w-4 h-4 text-slate-600" />
                  <span className="text-slate-700">Appeler l'agence</span>
                </a>
                <a 
                  href="mailto:contact@agence-msc.fr" 
                  className="flex items-center justify-center gap-2 p-3 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  <Mail className="w-4 h-4 text-slate-600" />
                  <span className="text-slate-700">Envoyer un email</span>
                </a>
              </div>
            </div>
            
            <div className="pt-4 border-t">
              <Button 
                variant="ghost" 
                onClick={logout}
                className="gap-2 text-slate-500"
              >
                <LogOut className="w-4 h-4" />
                Se déconnecter
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If no valid contract, show blocking screen
  if (accessStatus && !accessStatus.has_valid_contract) {
    const isSubcontractor = user?.team_member_type === 'subcontractor';
    const isEmployee = user?.team_member_type === 'employee' || !user?.team_member_type;
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            <CardTitle className="text-xl">
              {accessStatus.pending_contract_id ? "Contrat en attente de signature" : "Accès restreint"}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            {accessStatus.pending_contract_id ? (
              <p className="text-slate-600">
                Vous avez un contrat en attente de signature. Veuillez le signer pour débloquer votre espace de travail.
              </p>
            ) : isSubcontractor ? (
              <div className="space-y-2">
                <p className="text-slate-600">
                  Votre accès est actuellement restreint.
                </p>
                <p className="text-sm text-slate-500">
                  Aucun contrat actif n'est associé à votre compte. L'agence doit vous envoyer un nouveau contrat de mission avant que vous puissiez accéder à votre espace.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-slate-600">
                  Un nouveau contrat mensuel est requis pour débloquer votre compte.
                </p>
                <p className="text-sm text-slate-500">
                  Chaque mois, vous devez signer un nouveau contrat pour maintenir votre accès à l'espace de travail.
                </p>
              </div>
            )}
            
            {accessStatus.pending_contract_id && (
              <Button 
                onClick={() => navigate(`/my-member-contracts/${accessStatus.pending_contract_id}`)}
                className="w-full gap-2 text-white"
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <FileSignature className="w-4 h-4" />
                Signer mon contrat
              </Button>
            )}
            
            <Button 
              variant="outline" 
              onClick={() => navigate('/my-member-contracts')}
              className="w-full gap-2"
            >
              <FileSignature className="w-4 h-4" />
              Voir mes contrats
            </Button>
            
            <div className="pt-4 border-t">
              <p className="text-xs text-slate-400 mb-3">
                {isSubcontractor 
                  ? "En attente d'un contrat de l'agence"
                  : "Contactez l'agence si vous n'avez pas reçu votre contrat"
                }
              </p>
              <Button 
                variant="ghost" 
                onClick={logout}
                className="gap-2 text-slate-500"
              >
                <LogOut className="w-4 h-4" />
                Se déconnecter
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid contract and not suspended - render children
  return children;
};

export default ContractRequired;
